import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function EssexCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Essex Pothole Claims | GREEN Rated, Repairs Doubled | Fixtyer"
        description="Essex County Council is one of only 16 GREEN-rated highway authorities — yet its own report shows pothole repairs doubled to 18,636 a year, unclassified roads stuck at 10-11% RED, and a four-year machine-survey cycle on most of the network. What that means for your claim."
        canonical="/councils/essex"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Essex', url: '/councils/essex' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Essex is GREEN-rated — is a pothole claim even worth making?',
            answer: 'Yes, but it needs to be specific. The DfT rating measures the council\'s overall programme, not the individual defect that damaged your vehicle. Essex\'s own transparency report shows pothole repairs doubled from 9,293 in 2020/21 to 18,636 in 2024/25 — defects are forming on this network faster than ever. A claim against a GREEN council succeeds on evidence about your pothole: prior reports, photographs showing its size and age, and the inspection history of that road.',
          },
          {
            question: 'What does Essex\'s GREEN rating actually measure?',
            answer: 'The DfT scores each authority on road condition, spend and best practice, then combines them into an overall rating. Essex is GREEN overall, with GREEN condition and spend scorecards — but its best practice scorecard is AMBER, the one mark against it. GREEN also means "performing well relative to other councils", not "defect-free": Essex\'s own SCANNER data shows condition has stayed flat for five years rather than improving, with 10-11% of unclassified roads in RED condition every single year.',
          },
          {
            question: 'What if my pothole was on an unclassified or rural road?',
            answer: 'That is where Essex is weakest. Unclassified roads make up 4,540km — 60% of the 7,532km network — and 10-11% of them have sat in RED condition in every one of the last five surveys. Machine surveys cover only 25% of the unclassified network per year, in one direction only, so a full pass takes four years. And the council\'s own report admits many rural unclassified roads "have very little specifically engineered structure to them" and "are not as resilient as modern roads".',
          },
          {
            question: 'Why did pothole repairs double if Essex is so well run?',
            answer: 'Essex filled 18,636 potholes in 2024/25 — double the 9,293 of 2020/21 and nearly triple the 6,888 of 2021/22 — despite record spending. The council\'s report explains the mechanics: oxidisation makes road surfaces brittle, water penetration and freeze/thaw action create potholes, and extreme weather accelerates all of it. A network producing potholes at that rate is one where defects routinely appear between inspections — which is exactly when prior-report evidence decides claims.',
          },
          {
            question: 'Do the extra £25m resurfacing and 15,104 Member-led repairs hurt my claim?',
            answer: 'No — read carefully, they help explain it. The Members\' Highways Initiative delivered 15,104 extra repairs in 2024/25, including 8,000 carriageway defect repairs, specifically so councillors could tackle "the issues that residents see and care about the most". On top of the 18,636 scheduled pothole repairs, that is roughly 26,600 carriageway defects fixed in one year — around 73 every day. A backlog that size is evidence that resident-visible defects were waiting for political intervention, not routine inspection.',
          },
          {
            question: 'How does the Section 58 defence work against Essex?',
            answer: 'Section 58 of the Highways Act 1980 lets a council defeat a claim by proving it took reasonable care — a documented inspection regime, properly followed. Expect Essex to run this defence competently. The counter is specificity: SCANNER machine surveys reach a given unclassified road only once every four years and only in one direction; C roads need four years for full two-way coverage; and the council\'s own website concedes "issues may come up between these inspections". If your pothole was reported before your incident — on Essex\'s own Tell Us / Track It system or FixMyStreet — the defence collapses, because Section 58 fails where the council had actual notice.',
          },
        ]}
      />
      <ServiceSchema 
        councilName="Essex County Council" 
        councilSlug="essex" 
        rating="green" 
      />
      <SpeakableSchema
        url="/councils/essex"
        headline="Essex Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        {/* Top chevrons */}
        <div 
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`
          }}
        />
        
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Essex</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="green" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>
          
          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Essex: GREEN-Rated, Yet Pothole Repairs Have Doubled
          </h1>
          
          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Essex County Council is one of only 16 highway authorities rated <strong className="text-emerald-400">GREEN</strong> by 
            the DfT — but its own transparency report shows pothole repairs 
            <strong className="text-rose-400"> doubled to 18,636 a year</strong>, unclassified roads stuck at 
            10-11% RED condition for five straight years, and machine surveys reaching most of the network 
            only once every four years. A GREEN rating raises the bar for your claim. It does not close the door.
          </p>

          {/* Key stat callout */}
          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">18,636</div>
            <div className="text-white font-medium mt-2">Potholes filled in 2024/25 — double 2020/21</div>
            <div className="text-brand-300 text-sm mt-1">
              Plus 8,000 extra carriageway defect repairs through the Members' Highways Initiative — 
              roughly 73 carriageway defects fixed every day across Essex's 7,532km network.
            </div>
          </div>
        </div>

        {/* Bottom chevrons */}
        <div 
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`
          }}
        />
      </section>

      {/* What GREEN actually means */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What a GREEN Rating Actually Means — And What It Doesn't
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment first: Essex earned this rating, and your claim strategy needs to respect that
          </p>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6">
              <div className="text-4xl font-bold text-emerald-600">1 of 16</div>
              <div className="text-sm text-emerald-900 font-medium mt-1">GREEN-rated authorities</div>
              <div className="text-xs text-surface-500 mt-2">
                Out of more than 100 English highway authorities scored by the DfT for 2025/26, only 16 achieved 
                an overall GREEN. Essex is one of them.
              </div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-4xl font-bold text-brand-900">7,532km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Roads maintained by ECC</div>
              <div className="text-xs text-surface-500 mt-2">
                Among the largest local authority networks in the country: 609km of A roads, 2,383km of B and C roads, 
                and 4,540km of unclassified roads — plus 6,000km of footways.
              </div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <div className="text-4xl font-bold text-amber-600">AMBER</div>
              <div className="text-sm text-amber-900 font-medium mt-1">Best Practice scorecard</div>
              <div className="text-xs text-surface-500 mt-2">
                The one mark against Essex on the DfT table. Condition and spend are GREEN; the way the 
                council evidences best practice is not.
              </div>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Honest Read</h3>
            <p className="text-brand-200 mb-4">
              We won't pretend otherwise: Essex spends more than double its DfT allocation, runs a genuinely 
              preventative maintenance strategy, and its A and B/C road condition compares well nationally. 
              A generic, template pothole claim against this council will likely lose.
            </p>
            <p className="text-white font-medium">
              But the DfT rates programmes, not potholes. Essex's own data shows a network producing defects 
              at a record rate, a 4,540km unclassified network that hasn't improved in five years, and machine 
              surveys that reach most roads once every four years. The rating doesn't decide your claim — 
              the evidence about <em>your</em> defect does.
            </p>
          </div>
        </div>
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows: Flat, Not Improving
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of SCANNER survey data from Essex's own transparency report — consistent on classified 
            roads, stuck on unclassified ones
          </p>

          {/* A-roads */}
          <div className="bg-white border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A roads (609km — 8% of network): steady, with a wobble</h3>
            <div className="bg-surface-50 rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020/21</td>
                    <td className="px-4 py-2 text-center">3%</td>
                    <td className="px-4 py-2 text-center">26%</td>
                    <td className="px-4 py-2 text-center">71%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2021/22</td>
                    <td className="px-4 py-2 text-center">3%</td>
                    <td className="px-4 py-2 text-center">26%</td>
                    <td className="px-4 py-2 text-center">71%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022/23</td>
                    <td className="px-4 py-2 text-center">3%</td>
                    <td className="px-4 py-2 text-center">24%</td>
                    <td className="px-4 py-2 text-center">73%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023/24</td>
                    <td className="px-4 py-2 text-center">3%</td>
                    <td className="px-4 py-2 text-center">25%</td>
                    <td className="px-4 py-2 text-center">72%</td>
                  </tr>
                  <tr className="bg-surface-50">
                    <td className="px-4 py-2 font-mono font-bold">2024/25</td>
                    <td className="px-4 py-2 text-center font-bold">3%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">28%</td>
                    <td className="px-4 py-2 text-center font-bold">69%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              RED has held at 3% for five years — genuinely good. But note the latest year: GREEN-condition 
              A roads fell from 73% to 69% in two years while AMBER climbed to 28%, its highest in the series. 
              More than a quarter of Essex's main roads may need maintenance soon.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-white border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">B and C roads (2,383km — 32% of network): flat</h3>
            <div className="bg-surface-50 rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020/21</td>
                    <td className="px-4 py-2 text-center">3%</td>
                    <td className="px-4 py-2 text-center">25%</td>
                    <td className="px-4 py-2 text-center">72%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2021/22</td>
                    <td className="px-4 py-2 text-center">2%</td>
                    <td className="px-4 py-2 text-center">23%</td>
                    <td className="px-4 py-2 text-center">75%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022/23</td>
                    <td className="px-4 py-2 text-center">3%</td>
                    <td className="px-4 py-2 text-center">24%</td>
                    <td className="px-4 py-2 text-center">73%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023/24</td>
                    <td className="px-4 py-2 text-center">3%</td>
                    <td className="px-4 py-2 text-center">24%</td>
                    <td className="px-4 py-2 text-center">73%</td>
                  </tr>
                  <tr className="bg-surface-50">
                    <td className="px-4 py-2 font-mono font-bold">2024/25</td>
                    <td className="px-4 py-2 text-center font-bold">3%</td>
                    <td className="px-4 py-2 text-center font-bold">25%</td>
                    <td className="px-4 py-2 text-center font-bold">72%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              2024/25 is identical to 2020/21: 3% RED, 25% AMBER, 72% GREEN. Five years and record spending 
              have held the line — they haven't moved it. Roughly 28% of the B/C network needs, or may soon 
              need, maintenance. That is around 667km of road.
            </p>
          </div>

          {/* U roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">Unclassified roads (4,540km — 60% of network): stuck at 10-11% RED</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020/21</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">11%</td>
                    <td className="px-4 py-2 text-center">22%</td>
                    <td className="px-4 py-2 text-center">67%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021/22</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">11%</td>
                    <td className="px-4 py-2 text-center">23%</td>
                    <td className="px-4 py-2 text-center">66%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022/23</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">10%</td>
                    <td className="px-4 py-2 text-center">22%</td>
                    <td className="px-4 py-2 text-center">68%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023/24</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">11%</td>
                    <td className="px-4 py-2 text-center">23%</td>
                    <td className="px-4 py-2 text-center">66%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024/25</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">10%</td>
                    <td className="px-4 py-2 text-center font-bold">22%</td>
                    <td className="px-4 py-2 text-center font-bold">68%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              One in ten unclassified roads — roughly 450km of residential streets, estate roads and rural 
              lanes — has sat in RED condition in every survey for five years. On the road class that makes 
              up 60% of the network, the GREEN-rated programme has not moved the needle at all.
            </p>
          </div>

          <QuoteCard 
            quote="The data shows that road condition has remained relatively consistent. This is due to detailed data analysis and road condition prediction modelling, which help align investment decisions with required outcomes."
            source="Essex County Council Local Highways Maintenance Transparency Report, June 2025"
          />
          <p className="text-surface-600 text-sm mt-4">
            That's the council's own framing: the strategy is to hold condition <em>consistent</em>, not to 
            improve it. Consistent includes 450km of unclassified road consistently RED.
          </p>
        </div>
      </section>

      {/* The Four-Year Survey Cycle */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The Four-Year Survey Cycle
          </h2>
          <p className="text-surface-600 mb-8">
            How often does a SCANNER machine survey actually reach your road? Essex publishes the answer — 
            and for most of the network, it's measured in years
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Road class</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Share of network</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Annual SCANNER coverage</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Full coverage takes</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">A roads (609km)</td>
                  <td className="px-6 py-4 text-center">8%</td>
                  <td className="px-6 py-4 text-center">50% per year, both directions</td>
                  <td className="px-6 py-4 text-center font-medium">2 years</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">B roads</td>
                  <td className="px-6 py-4 text-center">32% (with C)</td>
                  <td className="px-6 py-4 text-center">50% per year, both directions</td>
                  <td className="px-6 py-4 text-center font-medium">2 years</td>
                </tr>
                <tr className="border-b border-surface-100 bg-amber-50">
                  <td className="px-6 py-4 font-mono text-brand-900">C roads</td>
                  <td className="px-6 py-4 text-center">—</td>
                  <td className="px-6 py-4 text-center">50% per year, <strong>one direction only</strong></td>
                  <td className="px-6 py-4 text-center font-bold text-amber-700">4 years (both directions)</td>
                </tr>
                <tr className="bg-rose-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">Unclassified (4,540km)</td>
                  <td className="px-6 py-4 text-center font-bold">60%</td>
                  <td className="px-6 py-4 text-center font-bold">25% per year, <strong>one direction only</strong></td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">4 years (one direction)</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What This Means In Practice</h3>
              <p className="text-surface-600 text-sm mb-3">
                On the unclassified network — 60% of all Essex roads — the structural condition survey reaches 
                a given road <strong>once every four years, in one direction</strong>. The lane on the other 
                side of your residential street may never have been machine-surveyed at all under the 
                published regime.
              </p>
              <p className="text-surface-600 text-sm">
                Between machine surveys, Essex relies on routine safety inspections and resident reports — 
                the council's website says plainly that "issues may come up between these inspections".
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The Rural Road Admission</h3>
              <p className="text-amber-800 text-sm mb-3">
                The report concedes that many unclassified roads, "particularly rural roads, are not of modern 
                construction, have evolved over time and have very little specifically engineered structure to 
                them, and are not as resilient as modern roads and may therefore require strengthening 
                treatments much sooner."
              </p>
              <p className="text-amber-800 text-sm">
                That is documented knowledge that the most thinly-surveyed road class is also the most 
                structurally fragile one.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, Essex must show it had a reasonable system for knowing the 
              condition of the road that damaged your vehicle. For an unclassified road, the questions write themselves:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• When did a SCANNER survey last cover your road — and in which direction?</li>
              <li>• If 10-11% of U roads have been RED for five straight years, was yours one of them?</li>
              <li>• What was the routine safety inspection frequency for your road, and was it followed?</li>
              <li>• The council admits rural U roads deteriorate faster — did inspection frequency reflect that?</li>
            </ul>
            <p className="text-white font-medium">
              A four-year, one-direction survey cycle on a road class the council itself describes as 
              structurally fragile is fertile ground for a well-evidenced claim.
            </p>
          </div>
        </div>
      </section>

      {/* The Repair Conveyor Belt */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            56,259 Potholes In Five Years — And The Trend Is Up
          </h2>
          <p className="text-surface-600 mb-8">
            Essex's own Confirm asset management system records the pothole count — and it has doubled
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Change vs 2020/21</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">9,293</td>
                  <td className="px-6 py-4 text-center text-surface-400">—</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">6,888</td>
                  <td className="px-6 py-4 text-center text-emerald-600">−26%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">8,221</td>
                  <td className="px-6 py-4 text-center text-emerald-600">−12%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-600">13,221</td>
                  <td className="px-6 py-4 text-center text-amber-600">+42%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">18,636</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">+101%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">56,259</td>
                  <td className="px-6 py-4 text-center text-surface-400">—</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Doubled In Five Years</h3>
              <p className="text-surface-600 text-sm">
                18,636 potholes filled in 2024/25 is double 2020/21 and nearly triple the 2021/22 low — 
                about 51 potholes every day. The council's report explains why: oxidisation makes surfaces 
                brittle, water penetration delaminates surface layers "creating potholes, which is especially 
                prevalent during winter months through 'freeze/thaw' action". The network is producing 
                defects faster than at any point in the published record.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Plus 15,104 Member-Led Repairs</h3>
              <p className="text-amber-800 text-sm">
                On top of routine maintenance, the Members' Highways Initiative completed 15,104 extra repairs 
                in 2024/25 — including 8,000 carriageway defect repairs "such as potholes and surface damage" 
                and 6,000 footway repairs. Add those to the scheduled count and Essex fixed roughly 
                <strong> 26,600 carriageway defects in a single year — around 73 a day</strong>.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The MHI Tells You Something</h3>
            <QuoteCard 
              quote="We launched the Member's Highways Initiative to give power to the people who know their communities best – our County Councillors. The results speak for themselves, with over 15,000 extra repairs this year alone, tackling the issues that residents see and care about the most."
              source="Cllr Tom Cunningham, Cabinet Member for Highways, Essex County Council, quoted in the Transparency Report"
            />
            <p className="text-brand-200 mt-6">
              Read that as a claimant: 15,000 defects serious enough for councillors to commission repairs 
              were sitting on the network, visible to residents, waiting for political intervention rather 
              than routine inspection to catch them. If your pothole was one of "the issues that residents 
              see" — and someone reported it before your incident — the Section 58 defence is in real trouble.
            </p>
          </div>
        </div>
      </section>

      {/* The Money */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The Money: Double The Allocation, Still 18,636 Potholes
          </h2>
          <p className="text-surface-600 mb-8">
            Essex's spend is genuinely impressive — which makes the pothole numbers more telling, not less
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">DfT capital allocation</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Actual capital spend</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Revenue spend</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Preventative share</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">£48.1m</td>
                  <td className="px-6 py-4 text-center">£75.5m</td>
                  <td className="px-6 py-4 text-center">£34.1m</td>
                  <td className="px-6 py-4 text-center">70%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">£37.0m</td>
                  <td className="px-6 py-4 text-center">£80.2m</td>
                  <td className="px-6 py-4 text-center">£26.8m</td>
                  <td className="px-6 py-4 text-center">67%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">£37.3m</td>
                  <td className="px-6 py-4 text-center">£81.9m</td>
                  <td className="px-6 py-4 text-center">£26.4m</td>
                  <td className="px-6 py-4 text-center">64%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center">£46.3m</td>
                  <td className="px-6 py-4 text-center">£95.4m</td>
                  <td className="px-6 py-4 text-center">£28.4m</td>
                  <td className="px-6 py-4 text-center">73%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">£41.0m</td>
                  <td className="px-6 py-4 text-center font-bold">£109.6m</td>
                  <td className="px-6 py-4 text-center">£33.4m</td>
                  <td className="px-6 py-4 text-center">78%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2025/26 (proj.)</td>
                  <td className="px-6 py-4 text-center font-bold">£52.3m</td>
                  <td className="px-6 py-4 text-center font-bold text-emerald-600">£111.1m</td>
                  <td className="px-6 py-4 text-center font-bold">£40.3m</td>
                  <td className="px-6 py-4 text-center font-bold">78%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">And This Is The Well-Funded Version</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£111.1m</div>
                <div className="text-brand-300 text-sm">Projected capital spend 2025/26 — more than double the £52.3m DfT allocation</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">78%</div>
                <div className="text-brand-300 text-sm">Estimated share of capital spent on preventative maintenance</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">~8%</div>
                <div className="text-brand-300 text-sm">Share of reactive maintenance funding spent on filling potholes</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Capital spend has risen every single year — from £75.5m to a projected £111.1m — and pothole 
              repairs <span className="text-rose-400">doubled anyway</span>. The chequebook is not the problem. 
              The network produces defects faster than even a GREEN-rated programme can prevent them.
            </p>
          </div>
        </div>
      </section>

      {/* The AMBER scorecard + admissions */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The AMBER Scorecard And The Admissions That Matter
          </h2>
          <p className="text-surface-600 mb-8">
            Essex's one non-GREEN DfT score — and what the council says in its own words
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Best Practice: AMBER</h3>
              <p className="text-amber-800 text-sm mb-3">
                On the DfT's 2025/26 table, Essex scores GREEN for condition and spend but only 
                <strong> AMBER for best practice</strong>. The transparency report leans heavily on innovation 
                trials — graphene resurfacing, AI defect detection, drone bridge inspections — yet the DfT 
                still found the council's evidenced practice short of the GREEN bar.
              </p>
              <p className="text-amber-800 text-sm">
                In a dispute, that is the official assessment that Essex's processes — the very thing a 
                Section 58 defence is built on — have documented room for improvement.
              </p>
            </div>
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The Methodology Change Ahead</h3>
              <p className="text-surface-600 text-sm mb-3">
                From 2026/27, Essex moves to the new PAS 2161 standard, which scores roads in five categories 
                instead of three. The council notes authorities "will be required to use a supplier that has 
                been accredited against PAS2161".
              </p>
              <p className="text-surface-600 text-sm">
                When that switch happens, today's five-year SCANNER series stops being comparable — worth 
                remembering if future condition figures suddenly look different.
              </p>
            </div>
          </div>

          <div className="space-y-6">
            <QuoteCard 
              quote="We carry out regular inspections of roads and pavements on our network, but we know that issues may come up between these inspections."
              source="Essex Highways Service Information Centre, cited in the Transparency Report"
            />
            <QuoteCard 
              quote="Many Unclassified roads, however, particularly rural roads, are not of modern construction, have evolved over time and have very little specifically engineered structure to them, and are not as resilient as modern roads and may therefore require strengthening treatments much sooner."
              source="Essex County Council Local Highways Maintenance Transparency Report, June 2025"
            />
          </div>

          <div className="bg-white rounded-xl p-6 border border-surface-200 mt-6">
            <h3 className="font-semibold text-brand-900 mb-3">Why These Two Quotes Matter Together</h3>
            <p className="text-surface-600 text-sm">
              The first is an admission that defects form between inspections — the precise gap into which 
              most pothole claims fall. The second is documented knowledge that the largest, least-surveyed 
              road class deteriorates fastest. Under Section 58, a council's inspection regime must be 
              reasonable <em>given what it knows</em>. Essex knows its rural unclassified roads are fragile, 
              and surveys them by machine once every four years, in one direction.
            </p>
          </div>
        </div>
      </section>

      {/* Claiming against a GREEN council */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a GREEN Council: The Straight Answer
          </h2>
          <p className="text-surface-600 mb-8">
            Essex will run a competent Section 58 defence. Here's the honest balance sheet — and how to win anyway
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> One of only 16 GREEN-rated authorities in England for 2025/26
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Spends more than double its DfT allocation — £111.1m projected vs £52.3m allocated
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 78% of capital spend classed as preventative — genuine "prevention first" strategy
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> A and B/C road RED share held at 2-3% for five years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented systems: Confirm defect records, condition modelling, Track It reporting
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect well-kept inspection records and a properly argued defence. A vague claim with one 
                blurry photo will not survive contact.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Pothole repairs doubled to 18,636 a year — defects form faster than prevention catches them
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 10-11% of unclassified roads RED in every survey for five years — about 450km
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Machine surveys reach U roads once every 4 years, one direction only — 60% of the network
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER best practice scorecard — the DfT's own caveat on Essex's processes
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 15,104 extra Member-led repairs in one year — a visible, resident-reported backlog
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Admits defects arise between inspections and rural U roads lack engineered structure
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Notice</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN condition and spend scorecards, your claim lives or dies on 
              <strong className="text-white"> actual or constructive notice of the specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole — Essex's own Tell Us / Track It system, FixMyStreet, councillor reports. A pre-incident report defeats Section 58 outright.</li>
              <li>• Photos showing size, depth and visible age — weathered edges, previous patching, accumulated debris all prove the defect predated the last inspection.</li>
              <li>• The road's class — on an unclassified road, the four-year one-direction survey cycle is your strongest structural argument.</li>
              <li>• Rural location — pair it with the council's own admission about non-engineered rural roads.</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and 
              cites Essex's own transparency data where it helps you — including the repair figures that 
              prove this network produces potholes at a record rate.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Essex?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A GREEN-rated council demands a properly evidenced claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No four-year survey-cycle argument</li>
                <li>• No prior-report search</li>
                <li>• No answer to a polished Section 58 defence</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 18,636 potholes filled in 2024/25 cited</li>
                <li>✅ Four-year U-road survey cycle argued</li>
                <li>✅ Rural-road structural admission quoted</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Essex</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=essex"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Essex Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Essex is GREEN-rated — is a pothole claim even worth making?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes, but it needs to be specific. The DfT rating measures the council's overall programme, 
                not the individual defect that damaged your vehicle. Essex's own transparency report shows 
                pothole repairs doubled from 9,293 in 2020/21 to 18,636 in 2024/25 — defects are forming on 
                this network faster than ever. A claim against a GREEN council succeeds on evidence about 
                your pothole: prior reports, photographs showing its size and age, and the inspection history 
                of that road.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What does Essex's GREEN rating actually measure?
              </h3>
              <p className="text-surface-600 text-sm">
                The DfT scores each authority on road condition, spend and best practice, then combines them 
                into an overall rating. Essex is GREEN overall, with GREEN condition and spend scorecards — 
                but its best practice scorecard is AMBER, the one mark against it. GREEN also means 
                "performing well relative to other councils", not "defect-free": Essex's own SCANNER data 
                shows condition has stayed flat for five years rather than improving, with 10-11% of 
                unclassified roads in RED condition every single year.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on an unclassified or rural road?
              </h3>
              <p className="text-surface-600 text-sm">
                That is where Essex is weakest. Unclassified roads make up 4,540km — 60% of the 7,532km 
                network — and 10-11% of them have sat in RED condition in every one of the last five surveys. 
                Machine surveys cover only 25% of the unclassified network per year, in one direction only, 
                so a full pass takes four years. And the council's own report admits many rural unclassified 
                roads "have very little specifically engineered structure to them" and "are not as resilient 
                as modern roads".
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Why did pothole repairs double if Essex is so well run?
              </h3>
              <p className="text-surface-600 text-sm">
                Essex filled 18,636 potholes in 2024/25 — double the 9,293 of 2020/21 and nearly triple the 
                6,888 of 2021/22 — despite record spending. The council's report explains the mechanics: 
                oxidisation makes road surfaces brittle, water penetration and freeze/thaw action create 
                potholes, and extreme weather accelerates all of it. A network producing potholes at that 
                rate is one where defects routinely appear between inspections — which is exactly when 
                prior-report evidence decides claims.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Do the extra £25m resurfacing and 15,104 Member-led repairs hurt my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                No — read carefully, they help explain it. The Members' Highways Initiative delivered 15,104 
                extra repairs in 2024/25, including 8,000 carriageway defect repairs, specifically so 
                councillors could tackle "the issues that residents see and care about the most". On top of 
                the 18,636 scheduled pothole repairs, that is roughly 26,600 carriageway defects fixed in one 
                year — around 73 every day. A backlog that size is evidence that resident-visible defects 
                were waiting for political intervention, not routine inspection.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                How does the Section 58 defence work against Essex?
              </h3>
              <p className="text-surface-600 text-sm">
                Section 58 of the Highways Act 1980 lets a council defeat a claim by proving it took 
                reasonable care — a documented inspection regime, properly followed. Expect Essex to run this 
                defence competently. The counter is specificity: SCANNER machine surveys reach a given 
                unclassified road only once every four years and only in one direction; C roads need four 
                years for full two-way coverage; and the council's own website concedes "issues may come up 
                between these inspections". If your pothole was reported before your incident — on Essex's 
                own Tell Us / Track It system or FixMyStreet — the defence collapses, because Section 58 
                fails where the council had actual notice.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources: <a 
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a> | <a 
              href="https://www.essexhighways.org/uploads/downloads/funding/essex-county-council-transparency-report-annex-a-12-june-25.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Essex County Council Local Highways Maintenance Transparency Report (June 2025)
            </a>. 
            Contains public sector information licensed under the{' '}
            <a href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/" target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline">
              Open Government Licence v3.0
            </a>.
          </p>
        </div>
      </section>
    </div>
  );
}
